/**
 * Contact-list member storage: paths, shard assignment, and the stored shape
 * of a member shard.
 *
 * A contact list is `email-contact-lists/{courseId}/docs/{listId}`. Its
 * members used to live entirely in that document's `contacts` array, which
 * caps a list at what one Firestore document can hold — an indexed array
 * spends one index entry per element against the 40,000-per-document limit,
 * and every edit rewrites the whole array. Operators hit the ceiling and
 * worked around it by hand, splitting one audience across documents named
 * `(1/3)`, `(2/3)`, `(3/3)` and re-selecting all three on every send.
 *
 * Members now live in shards under the list:
 * `email-contact-lists/{courseId}/docs/{listId}/memberShards/{shardId}`. A
 * member's shard is `hash(normalizedEmail) % shardCount`, so:
 *
 * - **Reading a list is a fixed cost.** `shardCount` reads, whatever the size.
 * - **Adding a member is a targeted write.** One `arrayUnion` on one shard,
 *   which is idempotent — re-importing an overlapping file cannot duplicate
 *   anyone, and no read-modify-write is needed to prevent it.
 * - **The shard count is per list, not global.** It is stored on the list
 *   document, so {@link DEFAULT_MEMBERS_PER_SHARD} can change later without
 *   re-sharding lists that already exist.
 *
 * **Members are not all email addresses.** A list entry is an email address, a
 * Firebase Auth uid, or another list's id — the send flow resolves uids and
 * nested lists down to addresses at send time. Most large lists in production
 * hold uids rather than addresses, so this is the common case, not an edge
 * case. Uids and list ids are case-sensitive, so entries are canonicalized by
 * {@link canonicalizeContactListMember}, which lowercases only what is
 * actually an address.
 *
 * This module is Firestore-SDK-free — paths and pure functions only — so the
 * webapp (client SDK) and functions (admin SDK) can share it. Reads and
 * writes live with their caller.
 *
 * @module
 */

import {z} from 'zod';

import {hashStringTo32BitSha256} from '../../hash';
import {isValidEmailAddress, normalizeEmail} from '../../utils/email';
import {FirestoreTimestampSchema} from '../types';

// ============================================================================
// Sizing
// ============================================================================

/**
 * Members a shard is filled to when a list is (re)written whole.
 *
 * Deliberately well under {@link MAX_MEMBERS_PER_SHARD}: the gap is headroom
 * for later appends, which add to existing shards rather than re-sharding. At
 * this target a shard holds roughly a tenth of what it could, so a list can
 * roughly double through appends before it needs re-sharding.
 */
export const DEFAULT_MEMBERS_PER_SHARD = 5_000;

/**
 * Hard ceiling on one shard's `members` array.
 *
 * Sized against the two Firestore limits an array field runs into — the 1 MiB
 * document cap and the 40,000 index-entries-per-document cap, which an indexed
 * array charges one entry per element against. At the entry lengths these
 * lists actually hold it spends well under half of either budget, leaving room
 * for longer entries and for any per-shard metadata added later.
 */
export const MAX_MEMBERS_PER_SHARD = 10_000;

/**
 * Largest shard count a list will be given.
 *
 * Caps the read cost of resolving one list. With
 * {@link MAX_MEMBERS_PER_SHARD} this puts a list's ceiling at a million
 * addresses — an order of magnitude past the largest audience the platform
 * has ever assembled, and the point at which a contact list is the wrong tool
 * anyway.
 */
export const MAX_CONTACT_LIST_SHARDS = 100;

// ============================================================================
// Member shard document
// ============================================================================

/**
 * Base schema for one member shard. Uses {@link FirestoreTimestampSchema} so
 * the admin and client modules can each apply their own timestamp transform.
 */
export const ContactListMemberShardDocumentSchema = z
  .object({
    /**
     * Canonicalized member entries assigned to this shard — addresses, uids,
     * or nested list ids.
     *
     * Every entry is the output of {@link canonicalizeContactListMember},
     * because that is what shard assignment hashes. Storing a raw entry here
     * would put it in a shard no lookup would search.
     */
    members: z.array(z.string()),
    /** When this shard was last written. */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link ContactListMemberShardDocumentSchema}. */
export type ContactListMemberShardDocument = z.infer<
  typeof ContactListMemberShardDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/** Top-level collection holding one document per course. */
export const EMAIL_CONTACT_LIST_COLLECTION = 'email-contact-lists' as const;

/** Subcollection under a course holding its contact lists. */
export const EMAIL_CONTACT_LIST_DOCS_SUBCOLLECTION = 'docs' as const;

/** Subcollection under a contact list holding its member shards. */
export const CONTACT_LIST_MEMBER_SHARDS_SUBCOLLECTION = 'memberShards' as const;

/**
 * Path of a course's contact-list collection.
 *
 * @param params - Lookup parameters.
 * @param params.courseId - Course owning the lists.
 * @return The collection path `email-contact-lists/{courseId}/docs`.
 */
export function getContactListCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${EMAIL_CONTACT_LIST_COLLECTION}/${courseId}/${EMAIL_CONTACT_LIST_DOCS_SUBCOLLECTION}`;
}

/**
 * Path of one contact list document.
 *
 * @param params - Lookup parameters.
 * @param params.courseId - Course owning the list.
 * @param params.listId - Firestore id of the list.
 * @return The document path
 *   `email-contact-lists/{courseId}/docs/{listId}`.
 */
export function getContactListDocumentPath({
  courseId,
  listId,
}: {
  courseId: string;
  listId: string;
}): string {
  return `${getContactListCollectionPath({courseId})}/${listId}`;
}

/**
 * Path of a contact list's member-shard collection.
 *
 * @param params - Lookup parameters.
 * @param params.courseId - Course owning the list.
 * @param params.listId - Firestore id of the list.
 * @return The collection path
 *   `email-contact-lists/{courseId}/docs/{listId}/memberShards`.
 */
export function getContactListMemberShardCollectionPath({
  courseId,
  listId,
}: {
  courseId: string;
  listId: string;
}): string {
  return `${getContactListDocumentPath({courseId, listId})}/${CONTACT_LIST_MEMBER_SHARDS_SUBCOLLECTION}`;
}

/**
 * Document id of one member shard.
 *
 * Zero-padded so shards sort lexicographically in the same order as
 * numerically, which is how the Firestore console and any `__name__`-ordered
 * query will list them.
 *
 * @param params - Lookup parameters.
 * @param params.shardIndex - Zero-based shard index; must be non-negative.
 * @return The shard document id, e.g. `shard-007`.
 */
export function getContactListMemberShardId({
  shardIndex,
}: {
  shardIndex: number;
}): string {
  return `shard-${String(shardIndex).padStart(3, '0')}`;
}

/**
 * Path of one member shard.
 *
 * @param params - Lookup parameters.
 * @param params.courseId - Course owning the list.
 * @param params.listId - Firestore id of the list.
 * @param params.shardIndex - Zero-based shard index.
 * @return The document path
 *   `email-contact-lists/{courseId}/docs/{listId}/memberShards/{shardId}`.
 */
export function getContactListMemberShardDocumentPath({
  courseId,
  listId,
  shardIndex,
}: {
  courseId: string;
  listId: string;
  shardIndex: number;
}): string {
  return `${getContactListMemberShardCollectionPath({courseId, listId})}/${getContactListMemberShardId({shardIndex})}`;
}

// ============================================================================
// Shard assignment
// ============================================================================

/**
 * Chooses how many shards a list of a given size should be written across.
 *
 * @param params - Sizing parameters.
 * @param params.memberCount - Number of members about to be written; negative
 *   values are treated as zero.
 * @return A shard count in the closed interval
 *   `[1, MAX_CONTACT_LIST_SHARDS]` — always at least one, so an empty list
 *   still has a shard to read.
 * @throws Never.
 */
export function computeContactListShardCount({
  memberCount,
}: {
  memberCount: number;
}): number {
  const wanted = Math.ceil(
    Math.max(memberCount, 0) / DEFAULT_MEMBERS_PER_SHARD,
  );
  return Math.min(Math.max(wanted, 1), MAX_CONTACT_LIST_SHARDS);
}

/**
 * Canonicalizes a list entry into the form that is stored and hashed.
 *
 * Email addresses are lowercased and trimmed, so `Ada@Example.COM ` and
 * `ada@example.com` become one member. Everything else — Firebase Auth uids
 * and nested contact-list ids — is only trimmed, because those identifiers
 * are case-sensitive and lowercasing them would produce a uid that resolves
 * to nobody. Production lists are mostly uids, so this distinction is the
 * common case, not an edge case.
 *
 * @param params - Canonicalization input.
 * @param params.entry - Raw list entry.
 * @return The canonical form. Blank or whitespace-only input yields `''`,
 *   which callers drop.
 * @throws Never.
 */
export function canonicalizeContactListMember({
  entry,
}: {
  entry: string;
}): string {
  const trimmed = entry.trim();
  return isValidEmailAddress(trimmed) ? normalizeEmail(trimmed) : trimmed;
}

/**
 * Assigns a member to a shard.
 *
 * The entry is canonicalized before hashing, so the two spellings of one
 * address land in the same shard — which is what makes that shard's
 * `arrayUnion` de-duplicate them.
 *
 * @param params - Assignment parameters.
 * @param params.member - Raw list entry; canonicalized internally.
 * @param params.shardCount - The list's stored shard count; must be positive.
 * @return A shard index in the half-open interval `[0, shardCount)`.
 * @throws {RangeError} When `shardCount` is not a positive integer — a zero
 *   or negative count would otherwise silently produce `NaN` and route every
 *   member to a shard that is never read.
 */
export function getContactListShardIndex({
  member,
  shardCount,
}: {
  member: string;
  shardCount: number;
}): number {
  if (!Number.isInteger(shardCount) || shardCount < 1) {
    throw new RangeError(
      `shardCount must be a positive integer, received ${String(shardCount)}`,
    );
  }
  return (
    hashStringTo32BitSha256(canonicalizeContactListMember({entry: member})) %
    shardCount
  );
}

/**
 * Groups member entries into the shards they belong to.
 *
 * Canonicalizes, drops blanks, and de-duplicates across the whole input, so
 * the returned shards together hold each distinct member exactly once.
 *
 * @param params - Partition parameters.
 * @param params.members - Raw list entries in any order.
 * @param params.shardCount - The list's shard count; must be positive.
 * @return A map from shard index to that shard's members. Shards that would
 *   be empty are absent from the map rather than present with an empty array,
 *   so a caller writing a partial update touches only shards that changed;
 *   a caller replacing a list whole must clear the ones it skips.
 * @throws {RangeError} When `shardCount` is not a positive integer.
 */
export function partitionMembersIntoShards({
  members,
  shardCount,
}: {
  members: readonly string[];
  shardCount: number;
}): Map<number, string[]> {
  const shards = new Map<number, string[]>();
  const seen = new Set<string>();

  for (const raw of members) {
    const member = canonicalizeContactListMember({entry: raw});
    if (member === '' || seen.has(member)) {
      continue;
    }
    seen.add(member);

    const index = getContactListShardIndex({member, shardCount});
    const shard = shards.get(index);
    if (shard === undefined) {
      shards.set(index, [member]);
      continue;
    }
    shard.push(member);
  }

  return shards;
}
